/**
 * **WebGPU 경로** (2026-09-27~, 이전 중 — docs/ROADMAP.md 「WebGPU 로 옮기기」).
 *
 * `?gpu=1` 이고 브라우저에 WebGPU 가 있으면 three 의 WebGPURenderer 로 그린다. 실험(브랜치 ufo/webgpu-spike)에서
 * 점광 171개를 전부 켜고 ClusteredLighting 으로 맥북 M2 Pro 101~119fps — WebGL 슬롯 16개(50~78)보다 빨랐다.
 * 같은 조명 수에서도 draw call 비용이 작아 1.5~1.9배.
 *
 * 게임 코드는 아직 렌더러를 `THREE.WebGLRenderer` 타입으로 받는다. WebGPURenderer 에 없는 것(capabilities)을 메워
 * 그 모양으로 넘기고, 둘이 다르게 동작하는 것(그림자 갱신·후처리·픽셀 읽기)은 여기와 pipelineGPU.ts 가 가른다.
 *
 * ⚠ WebGPU 는 **https(보안 컨텍스트)에서만** 켜진다 — Tailscale http 주소로 열면 navigator.gpu 가 없어 WebGL 로 돈다.
 */
import * as THREE from 'three';

// Node 에서 도는 검사 도구(tools/audit-*.ts)도 outside.ts 를 거쳐 이 모듈을 불러온다 — location 이 없다
export const WANT_GPU = typeof location !== 'undefined' && new URLSearchParams(location.search).get('gpu') === '1';
/** 지금 WebGPU 로 그리는가. createRenderer 뒤에 정해진다 */
export let IS_GPU = false;
/**
 * WebGPU 에서만 쓰는 노드 재질들(gpuNodes.ts). **WebGL 로 도는 사람은 받지 않는다** — three/webgpu 는 크다.
 * createRenderer 가 WebGPU 를 고르면 채워진다. 그 뒤에 짓는 것(outside.ts 하늘 등)이 동기로 꺼내 쓴다
 */
export let GPU_NODES: typeof import('./gpuNodes') | null = null;
let GPU_MOD: typeof import('three/webgpu') | null = null;
/** 렌더러를 고르기 전에 만든 재질의 노드 얹기(onGpu). 고르면 비운다 */
let pending: [THREE.Material, (n: typeof import('./gpuNodes'), m: THREE.Material) => void][] | null = [];
/**
 * WebGPU 면 이 재질에 노드를 얹는다(gpuNodes.ts) — onBeforeCompile 개조의 WebGPU 판. WebGL 이면 아무것도 안 한다.
 * 렌더러를 고르기 전(모듈을 불러오는 중 등)에 만든 재질은 줄 세워 뒀다가 고르는 순간 얹는다.
 */
export function onGpu<M extends THREE.Material>(mat: M, fn: (n: typeof import('./gpuNodes'), m: M) => void): M {
  if (GPU_NODES) fn(GPU_NODES, mat);
  else pending?.push([mat, fn as (n: typeof import('./gpuNodes'), m: THREE.Material) => void]);
  return mat;
}

export async function createRenderer(canvas: HTMLCanvasElement): Promise<THREE.WebGLRenderer> {
  const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
  if (WANT_GPU && nav.gpu && (await nav.gpu.requestAdapter().catch(() => null))) {
    const [{ WebGPURenderer }, { ClusteredLighting }, nodes] = await Promise.all([
      import('three/webgpu'), import('three/addons/lighting/ClusteredLighting.js'), import('./gpuNodes'),
    ]);
    GPU_NODES = nodes;
    for (const [m, fn] of pending ?? []) fn(nodes, m);
    pending = null;
    GPU_MOD = await import('three/webgpu');
    const r = new WebGPURenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    // 점광은 전부 켜 둔다 — 화면을 칸으로 나눠 칸에 닿는 점광만 계산한다(그림자 없는 점광만. 스포트는 기본 경로).
    // **init 전에 꽂아야 한다.** init 이 렌더 리스트에 조명 시스템을 붙잡아 두어, 뒤에 바꾸면 기본 조명(점광마다 픽셀 계산)이
    // 그대로 쓰인다 — 점광 84개에 방 안 7fps 였고, 점광을 지하 1km 로 치워도 7fps 였다
    (r as unknown as { lighting: unknown }).lighting = new ClusteredLighting();
    await r.init();
    // 게임 코드가 부르는 WebGL 전용 API 를 메운다
    const rr = r as unknown as { capabilities?: unknown; getMaxAnisotropy?: () => number };
    rr.capabilities ??= { getMaxAnisotropy: () => rr.getMaxAnisotropy?.() ?? 8, isWebGL2: true };
    IS_GPU = true;
    console.info('[gpu] WebGPURenderer + ClusteredLighting');
    return r as unknown as THREE.WebGLRenderer;
  }
  pending = null;
  if (WANT_GPU) console.warn('[gpu] WebGPU 가 없다(https 가 아니거나 브라우저 미지원) — WebGL 로 그린다');
  return new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
}

/**
 * 그림자를 다시 굽는다. WebGL 은 렌더러 전체 스위치(`shadowMap.needsUpdate`, autoUpdate 는 main.ts 가 끈다),
 * WebGPU 는 **조명마다**다(`shadow.needsUpdate`, 렌더러 스위치가 없다). WebGPU 에서 조명의 autoUpdate 를
 * 끄지 않으면 매 프레임 굽는다 — 그림자 조명을 처음 볼 때 끈다.
 */
type ShadowLight = THREE.Light & { shadow: THREE.LightShadow };
const shadowLights = new Set<ShadowLight>();
let shadowScanAt = -1;
export function requestShadowUpdate(renderer: THREE.WebGLRenderer, scene: THREE.Scene) {
  if (!IS_GPU) { renderer.shadowMap.needsUpdate = true; return; }
  // 그림자 조명은 고정 슬롯이라 거의 안 바뀐다 — 2초에 한 번만 다시 훑는다
  const now = performance.now();
  if (now - shadowScanAt > 2000) {
    shadowScanAt = now;
    scene.traverse((o) => {
      const l = o as ShadowLight;
      if (l.isLight && l.castShadow && l.shadow && !shadowLights.has(l)) { l.shadow.autoUpdate = false; shadowLights.add(l); }
    });
  }
  for (const l of shadowLights) l.shadow.needsUpdate = true;
}

/**
 * 아직 노드로 못 옮긴 ShaderMaterial 은 WebGPU 가 못 그린다 — 숨긴다(지금은 없다: 하늘·능선·눈 모두 gpuNodes.ts).
 * onBeforeCompile 개조는 onGpu 로 노드를 얹는다. 시신 표면하산란(furniture/flesh.ts)만 못 옮겨 기본 물리 재질로 그려진다
 */
export function hideUnported(scene: THREE.Scene) {
  if (!IS_GPU) return 0;
  let n = 0;
  scene.traverse((o) => { const m = (o as THREE.Mesh).material as THREE.ShaderMaterial | undefined; if (m?.isShaderMaterial) { o.visible = false; n++; } });
  return n;
}

/**
 * WebGPU 가 셰이더(노드)를 아직 짓고 있는가. three r185 는 빌드를 대기열에 넣고 **끝날 때까지 그 물체를 건너뛰고** 그린다
 * (Nodes.getForRenderDeferred) — 내부 필드(_nodes._buildQueue)라 three 를 올리면 확인할 것
 */
export function gpuBusy(renderer: THREE.WebGLRenderer) {
  const n = (renderer as unknown as { _nodes?: { _buildQueue?: unknown[]; _buildInProgress?: boolean } })._nodes;
  return !!n && ((n._buildQueue?.length ?? 0) > 0 || !!n._buildInProgress);
}

/**
 * 로딩 중에 지을 셰이더가 남지 않을 때까지 그려 본다(WebGPU). WebGL 은 compileAsync 가 다 굽지만 WebGPU 는 본 패스·그림자·
 * 후처리의 변종을 실제로 그려 봐야 대기열에 오른다 — 안 하면 방에 처음 들어갈 때 몇 초씩 물체가 빠지거나 멎는다
 */
export async function drainBuilds(renderer: THREE.WebGLRenderer, frame: () => void, onWait?: (sec: number) => void, maxMs = 90000) {
  if (!IS_GPU) return;
  const t0 = performance.now();
  let idle = 0;
  while (performance.now() - t0 < maxMs && idle < 3) {
    frame();
    await new Promise((r) => requestAnimationFrame(r));
    idle = gpuBusy(renderer) ? 0 : idle + 1;
    onWait?.((performance.now() - t0) / 1000);
  }
}

/** 렌더 타깃 픽셀 읽기. WebGPU 는 비동기뿐이다 */
export async function readPixels(renderer: THREE.WebGLRenderer, rt: THREE.RenderTarget, w: number, h: number, into: Uint8Array) {
  if (!IS_GPU) { renderer.readRenderTargetPixels(rt as THREE.WebGLRenderTarget, 0, 0, w, h, into); return; }
  const r = renderer as unknown as { readRenderTargetPixelsAsync(rt: THREE.RenderTarget, x: number, y: number, w: number, h: number): Promise<ArrayLike<number>> };
  into.set(await r.readRenderTargetPixelsAsync(rt, 0, 0, w, h));
}

/**
 * **움직이지 않는 세계를 Render Bundle 로 묶는다** (2026-09-27). WebGPURenderer 는 물체마다 JS 비용이 커서
 * 조합 홀(draw call 1,256)에서 프레임당 JS 가 17ms 였다 — 번들은 그리기 명령을 한 번 기록해 두고 재생한다(17 → 8.6ms).
 *
 * 규칙(three r185 BundleGroup):
 *  - **기록할 때 컬링한다** — 그때 시야 밖이던 것은 빠진다. 그래서 번들 안은 frustumCulled 를 끈다(먼 방 가구는
 *    cullPieces 가 visible 로 끈다).
 *  - 위치·회전 변화(서랍·문)는 재생 때마다 반영된다. **visible·material 이 바뀌거나 자식이 들고 나면 다시 기록해야
 *    한다** — 아래에서 그 셋을 가로채 번들 version 을 올린다.
 *  - 소품도 넣는다 — 집으면 숨기고(visible) 처음 조준하면 재질을 복제해 바꾸지만(props.ts ownMaterials) 드물다. 소품이 걸린 그룹이
 *    조합 홀 메시의 대부분이라 빼면 번들이 소용없었다(메시 1,003 안 · 4,953 밖).
 *  - 빼는 것: 조명(번들 안 조명은 조명 목록에 안 든다), 커튼(매 프레임 컬링), **투명 재질** — 번들은 불투명·투명 목록보다
 *    **먼저** 그려진다. 번들 안 유리 뒤에 번들 밖 물체가 있으면 나중에 그려진 그 물체가 유리를 덮는다.
 */
export function bundleStatic(root: THREE.Object3D, renderer: THREE.WebGLRenderer) {
  if (!GPU_MOD) return null;
  const skip = (c: THREE.Object3D) => {
    let out = false;
    c.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if ((o as THREE.Light).isLight || o.name === 'curtain' || (m && (Array.isArray(m) ? m : [m]).some((x) => x.transparent))) out = true;
    });
    return out;
  };
  /*
   * **칸(TILE m)마다 번들 하나.** 번들 안은 컬링을 끄므로 한 덩어리로 묶으면 등 뒤까지 다 그려 GPU 가 늘었다(거리 100 → 90fps).
   * 칸 번들은 통째로 켜고 끈다(update) — 번들 자신의 visible 은 다시 기록을 부르지 않는다. 너무 큰 것(벽·바닥 병합)은 늘 켜는 번들로.
   */
  const TILE = 10;
  const tiles = new Map<string, { bundle: THREE.Group; box: THREE.Box3 }>();
  const always = new GPU_MOD.BundleGroup();
  always.name = 'bundle:always';
  const box = new THREE.Box3(), c3 = new THREE.Vector3(), size = new THREE.Vector3();
  const kids = root.children.filter((c) => !skip(c));
  for (const c of kids) {
    box.setFromObject(c);
    if (box.isEmpty() || box.getSize(size).length() > TILE * 2) { always.add(c); continue; }
    box.getCenter(c3);
    const key = `${Math.floor(c3.x / TILE)},${Math.floor(c3.z / TILE)}`;
    let t = tiles.get(key);
    if (!t) { const b = new GPU_MOD.BundleGroup(); b.name = `bundle:${key}`; t = { bundle: b, box: new THREE.Box3() }; tiles.set(key, t); }
    t.box.union(box);
    t.bundle.add(c);
  }
  root.add(always);
  let n = 0;
  for (const b of [always, ...[...tiles.values()].map((t) => t.bundle)]) {
    if (b !== always) root.add(b);
    b.traverse((o) => { if (o !== b) { watch(o, b as unknown as { needsUpdate: boolean }); n++; } });
  }
  // 서랍이 열리면 칸을 조금 넘는다 — 칸 상자를 넉넉히
  for (const t of tiles.values()) t.box.expandByScalar(1.5);
  const frustum = new THREE.Frustum(), m4 = new THREE.Matrix4();
  /*
   * **셰이더를 짓는 동안 기록된 번들은 그 물체가 빠진 채 굳는다.** WebGPU 는 노드 빌드가 안 끝난 물체를 건너뛰고 그린다 —
   * 번들이 그 순간 기록되면 다음 재기록까지 그 물체가 없다. 첫 실행에 벽이 빠져 문짝만 공중에 떠 있었다(2026-09-27).
   * 빌드 대기열이 비는 순간 모든 칸을 다시 기록한다. 처음은 「바빴다」로 시작해 첫 한가한 프레임에 한 번 기록한다
   */
  let wasBusy = true;
  const all = [always, ...[...tiles.values()].map((t) => t.bundle)] as unknown as { needsUpdate: boolean }[];
  /** 매 프레임: 시야에 걸리거나 가까운(그림자를 드리울 수 있는) 칸만 켠다 */
  const update = (camera: THREE.Camera, at: THREE.Vector3) => {
    if (gpuBusy(renderer)) wasBusy = true;
    else if (wasBusy) { wasBusy = false; for (const b of all) b.needsUpdate = true; }
    camera.updateMatrixWorld();
    frustum.setFromProjectionMatrix(m4.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    for (const t of tiles.values()) t.bundle.visible = t.box.distanceToPoint(at) < 14 || frustum.intersectsBox(t.box);
  };
  return { moved: kids.length, objects: n, tiles: tiles.size, update };
}

const watched = new WeakSet<THREE.Object3D>();
function watch(o: THREE.Object3D, bundle: { needsUpdate: boolean }) {
  o.frustumCulled = false;
  if (watched.has(o)) return;
  watched.add(o);
  const bump = () => { bundle.needsUpdate = true; };
  let vis = o.visible;
  Object.defineProperty(o, 'visible', { configurable: true, enumerable: true, get: () => vis, set: (v: boolean) => { if (v !== vis) { vis = v; bump(); } } });
  const mesh = o as THREE.Mesh;
  if (mesh.isMesh) {
    let mat = mesh.material;
    Object.defineProperty(mesh, 'material', { configurable: true, enumerable: true, get: () => mat, set: (v) => { if (v !== mat) { mat = v; bump(); } } });
  }
  const add = o.add.bind(o), remove = o.remove.bind(o);
  o.add = (...objs: THREE.Object3D[]) => { add(...objs); for (const c of objs) c.traverse((d) => watch(d, bundle)); bump(); return o; };
  o.remove = (...objs: THREE.Object3D[]) => { remove(...objs); bump(); return o; };
}
